/**
 * The assembler's floating view widget, top-right of the stage: the 2D / 3D
 * segmented control (the editor's own styles, with the view menu, the export
 * menu, and the settings gear), then one separated row each for the level
 * switcher and, in 3D mode, the editor's 3D navigation tools, which drive the
 * assembler's camera controls.
 */

import { css } from '@emotion/react';
import type { AssemblerMode, DescribeAssemblyRoom, ExportFormat } from '@kaiizen/kai-floor-plan-editor-core';
import { midtone, monoLabel } from '../../styles';
import { LevelSelect } from '../panel';
import { SettingsMenu } from '../SettingsMenu';
import { ViewMenu } from '../ViewMenu';
import { SEG_ACTIVE_STYLE, SEG_ROW_STYLE, SEG_STYLE, SUB_ROW_STYLE, TOP_ROW_STYLE, View3DToolsRow, VIEW_OVERLAY_STYLE } from '../ViewOverlay';
import { AssemblyExportMenu } from './AssemblyExportMenu';

const ASSEMBLY_LEVEL_ROW_STYLE = css`
  label: ASSEMBLY_LEVEL_ROW_STYLE;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  span {
    ${monoLabel}
    font-size: 10px; letter-spacing: 0.06em; color: ${midtone};
  }
`;

const SEGMENTS: [AssemblerMode, string][] = [
  ['2d', '2D Plan'],
  ['3d', '3D View'],
];

export interface AssemblyViewOverlayProps {
  mode: AssemblerMode;
  onMode: (mode: AssemblerMode) => void;
  level: number;
  /** Every level that has rooms (plus the default), ascending. */
  levels: readonly number[];
  onLevel: (level: number) => void;
  /** The export menu's file downloads: the current level as a 2D plan (DXF/SVG) or a 3D model (STEP/STL). */
  onExport: (format: ExportFormat) => void;
  /** What the export menu's JSON says about each room after its id; its name when omitted. */
  describeRoom?: DescribeAssemblyRoom;
  /** Adds the whole-plan JSON (download and copy) to the export menu. */
  advanced?: boolean;
}

export function AssemblyViewOverlay(
  { mode, onMode, level, levels, onLevel, onExport, describeRoom, advanced }: AssemblyViewOverlayProps,
): React.JSX.Element {
  return (
    <div css={VIEW_OVERLAY_STYLE}>
      <div css={TOP_ROW_STYLE}>
        <div css={SEG_ROW_STYLE} data-testid='view-toggle' role='group' aria-label='View mode'>
          {SEGMENTS.map(([segmentMode, label]) => (
            <button
              key={segmentMode}
              css={[SEG_STYLE, mode === segmentMode && SEG_ACTIVE_STYLE]}
              data-view={segmentMode}
              aria-pressed={mode === segmentMode}
              onClick={() => {
                if (mode !== segmentMode) onMode(segmentMode);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <ViewMenu roomLabels />
        <AssemblyExportMenu onExport={onExport} describeRoom={describeRoom} advanced={advanced} />
        <SettingsMenu />
      </div>
      <div css={SUB_ROW_STYLE}>
        <label css={ASSEMBLY_LEVEL_ROW_STYLE}>
          <span>Level</span>
          <LevelSelect value={level} levels={levels} onChange={onLevel} />
        </label>
      </div>
      {mode === '3d' && (
        <div css={SUB_ROW_STYLE}>
          <View3DToolsRow />
        </div>
      )}
    </div>
  );
}
